export const GOOGLE_LIVE_REFERENCE_SOURCE_URLS = Object.freeze([
  'https://developers.google.com/maps/documentation/places/web-service/policies',
  'https://developers.google.com/maps/documentation/places/web-service/place-photos',
  'https://developers.google.com/maps/documentation/places/web-service/review-summaries',
  'https://developers.google.com/maps/documentation/places/web-service/maps-links',
]);

export const CATALOG_FIELD_BOUNDARY = Object.freeze({
  appOwnedDurable: Object.freeze([
    'id',
    'name',
    'name_ar',
    'description',
    'description_ar',
    'editor_note',
    'editor_note_ar',
    'editorial_labels',
    'category',
    'village',
    'village_ar',
    'latitude',
    'longitude',
    'featured',
    'active',
    'source',
    'verified',
    'specialties',
    'event_date',
    'event_time',
    'location',
    'location_ar',
  ]),
  googleLiveReference: Object.freeze([
    'google_place_id',
    'rating',
    'price_range',
    'open_hours',
    'phone',
    'website',
  ]),
  googleDurableMedia: Object.freeze([
    'image_url',
    'photo_reference',
    'photoReference',
    'photo_name',
    'photoName',
    'photo_url',
    'photoUrl',
    'media_url',
    'mediaUrl',
    'photo_media_url',
    'photoMediaUrl',
    'author_attributions',
    'authorAttributions',
  ]),
  firstPartyMedia: Object.freeze([
    'image_url',
  ]),
});

export const FORBIDDEN_PERSISTED_GOOGLE_CONTENT_FIELDS = Object.freeze([
  'reviews',
  'review',
  'review_text',
  'reviewText',
  'review_summary',
  'reviewSummary',
  'editorial_summary',
  'editorialSummary',
  'generative_summary',
  'generativeSummary',
  'place_summary',
  'placeSummary',
]);

export const GOOGLE_ATTRIBUTION_PATTERNS = Object.freeze({
  detailsText: 'Details from Google',
  detailsWithLocalContextText: 'Local guide note with live Google details',
  logoAltText: 'Google',
  photoAuthorPrefix: 'Photo by',
  reviewAuthorPrefix: 'Review by',
  requiredWhenOutsideGoogleMap: 'Show Google attribution next to any live Google Places detail displayed outside a Google map.',
  photoAuthorRule: 'If a live Google photo is shown, show its author attribution with the image and keep the author link when provided.',
});

const GOOGLE_PLACE_PHOTO_URL_PATTERNS = Object.freeze([
  /googleapis\.com\/maps\/api\/place\/photo/i,
  /places\.googleapis\.com\/v1\/places\/[^/]+\/photos\/[^/]+\/media/i,
  /[?&](photo_reference|photoreference)=/i,
]);

const GOOGLE_HOSTED_MEDIA_URL_PATTERNS = Object.freeze([
  /googleusercontent\.com/i,
]);

function hasPersistedValue(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

export function isGooglePlacePhotoUrl(value) {
  if (typeof value !== 'string') return false;
  return GOOGLE_PLACE_PHOTO_URL_PATTERNS.some(pattern => pattern.test(value));
}

export function classifyCatalogImageUrl(value) {
  if (value == null || String(value).trim() === '') {
    return {
      status: 'missing',
      label: 'Missing image URL',
      needsFirstPartyReplacement: true,
      reason: 'Catalog row has no image_url.',
    };
  }

  const url = String(value);
  if (isGooglePlacePhotoUrl(url)) {
    return {
      status: 'google_place_photo',
      label: 'Google Places photo URL/reference',
      needsFirstPartyReplacement: false,
      reason: 'Catalog image_url is Google-sourced media and must be displayed with Google photo attribution.',
    };
  }

  if (GOOGLE_HOSTED_MEDIA_URL_PATTERNS.some(pattern => pattern.test(url))) {
    return {
      status: 'google_hosted_media',
      label: 'Google-hosted media URL',
      needsFirstPartyReplacement: false,
      reason: 'Catalog image_url is Google-hosted media and must be displayed with Google photo attribution.',
    };
  }

  return {
    status: 'first_party_or_external',
    label: 'First-party/operator or external URL',
    needsFirstPartyReplacement: false,
    reason: 'Image URL is not recognized as Google Places or Google-hosted media.',
  };
}

export function summarizeCatalogImageReplacement(rows) {
  const summary = {
    total: rows.length,
    missing: 0,
    googlePlacePhoto: 0,
    googleHostedMedia: 0,
    firstPartyOrExternal: 0,
    needsFirstPartyReplacement: 0,
  };

  for (const row of rows) {
    const classification = classifyCatalogImageUrl(row && row.image_url);
    if (classification.status === 'missing') summary.missing += 1;
    else if (classification.status === 'google_place_photo') summary.googlePlacePhoto += 1;
    else if (classification.status === 'google_hosted_media') summary.googleHostedMedia += 1;
    else summary.firstPartyOrExternal += 1;

    if (classification.needsFirstPartyReplacement) summary.needsFirstPartyReplacement += 1;
  }

  return summary;
}

export function findGoogleLiveReferencePersistenceViolations(row, options = {}) {
  const source = options.source || row?.source || null;
  const violations = [];

  for (const field of FORBIDDEN_PERSISTED_GOOGLE_CONTENT_FIELDS) {
    if (hasPersistedValue(row && row[field])) {
      violations.push({
        field,
        reason: 'Google Places reviews and Places-authored summaries must stay transient.',
      });
    }
  }

  if (source === 'google_research') {
    for (const field of ['description', 'description_ar']) {
      if (hasPersistedValue(row && row[field])) {
        violations.push({
          field,
          reason: 'Google research rows must not turn Places-authored summaries or review text into app-owned descriptions.',
        });
      }
    }
  }

  return violations;
}

export function assertCatalogRowGoogleLiveReferenceSafe(row, options = {}) {
  const violations = findGoogleLiveReferencePersistenceViolations(row, options);
  if (violations.length === 0) return;

  const name = options.itemName || row?.name || row?.google_place_id || 'catalog row';
  const fields = violations.map(violation => violation.field).join(', ');
  throw new Error(`Refusing to persist Google live-reference content for ${name}: ${fields}`);
}
